/**
 * Parser for STEPBible Strong's Lexicon data
 * Data source: https://github.com/STEPBible/STEPBible-Data
 * License: CC BY 4.0
 */

export interface StrongsEntry {
  strongsNum: string;       // e.g., "H1242" or "G123"
  word: string;             // Hebrew/Greek word
  transliteration: string;  // Romanized pronunciation
  morphology: string;       // Part of speech / morphology code
  gloss: string;            // Brief English gloss
  definition: string;       // Full definition (HTML format)
}

export class StrongsParser {
  private hebrewData: Map<string, StrongsEntry> = new Map();
  private greekData: Map<string, StrongsEntry> = new Map();
  private loaded = false;


  /**
   * Load and parse the Strong's lexicon files
   */
  async load(): Promise<void> {
    if (this.loaded) return;

    try {
      // Load Hebrew lexicon
      const hebrewResponse = await fetch('/data/strongs/TBESH-Hebrew.txt');
      const hebrewText = await hebrewResponse.text();
      this.parseFile(hebrewText, this.hebrewData, 'H');

      // Load Greek lexicon
      const greekResponse = await fetch('/data/strongs/TBESG-Greek.txt');
      const greekText = await greekResponse.text();
      this.parseFile(greekText, this.greekData, 'G');

      this.loaded = true;
      console.log(`Loaded ${this.hebrewData.size} Hebrew and ${this.greekData.size} Greek Strong's entries`);
    } catch (error) {
      console.error('Failed to load Strong\'s lexicon data:', error);
      throw error;
    }
  }

  /**
   * Parse a tab-delimited lexicon file
   */
  private parseFile(text: string, dataMap: Map<string, StrongsEntry>, prefix: 'H' | 'G'): void {
    const lines = text.split('\n');
    let dataStarted = false;
    let parsedCount = 0;

    for (const line of lines) {
      // Skip header lines until we find actual data
      if (!dataStarted) {
        if (line.startsWith(prefix)) {
          dataStarted = true;
        } else {
          continue;
        }
      }

      // Skip empty lines
      if (!line.trim()) continue;

      // Parse tab-delimited fields
      const fields = line.split('\t');
      if (fields.length < 8) continue; // Need at least 8 fields (0-7)

      const strongsNum = fields[0].trim();

      // Skip if not a valid Strong's number
      if (!strongsNum.match(/^[HG]\d{4}/)) continue;

      const entry: StrongsEntry = {
        strongsNum: strongsNum,
        word: fields[3]?.trim() || '',
        transliteration: fields[4]?.trim() || '',
        morphology: fields[5]?.trim() || '',
        gloss: fields[6]?.trim() || '',
        definition: fields[7]?.trim() || '',
      };

      // Store by the full Strong's number (e.g., H0776A)
      dataMap.set(strongsNum, entry);

      // Also store by just the base number without suffix for variants
      const baseNum = strongsNum.match(/^[HG]\d{4}/)?.[0];
      if (baseNum && !dataMap.has(baseNum)) {
        dataMap.set(baseNum, entry);
      }
    }
  }

  /**
   * Look up a Strong's number
   * @param strongsNum - Strong's number (e.g., "H1242", "G123")
   * @returns StrongsEntry or undefined if not found
   */
  lookup(strongsNum: string): StrongsEntry | undefined {
    if (!this.loaded) {
      console.warn('Strong\'s data not loaded yet. Call load() first.');
      return undefined;
    }

    // Normalize the input (ensure uppercase prefix)
    const normalized = strongsNum.toUpperCase().trim();

    if (normalized.startsWith('H')) {
      return this.hebrewData.get(normalized);
    } else if (normalized.startsWith('G')) {
      return this.greekData.get(normalized);
    }

    return undefined;
  }

  /**
   * Check if the parser has been loaded
   */
  isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Get all Hebrew entries
   */
  getAllHebrew(): Map<string, StrongsEntry> {
    return this.hebrewData;
  }

  /**
   * Get all Greek entries
   */
  getAllGreek(): Map<string, StrongsEntry> {
    return this.greekData;
  }
}

// Singleton instance
let strongsParserInstance: StrongsParser | null = null;

/**
 * Get the singleton instance of the StrongsParser
 */
export function getStrongsParser(): StrongsParser {
  if (!strongsParserInstance) {
    strongsParserInstance = new StrongsParser();
  }
  return strongsParserInstance;
}
